import Vue from "vue";
import messages from "./messages";
import { getLanguages } from "../recovered/index-api";

// 轻量国际化：
// - zh / en 词表仍写在各组件的 `i18n: { zh: {...}, en: {...} }` 里；
// - 其余语言集中放在 src/i18n/locales/<code>.js，按组件 name 分命名空间；
// - 语言清单以后台 /api/index/languages 为准，拉不到时用下面的内置列表兜底。

// 内置兜底清单：code 与后台 code 对齐后会被整体替换
// code=传给后端的 lang，value=本地词表键，label=菜单文案，flag=旗帜
export const FALLBACK_LOCALES = [
  { code: "en", value: "en", label: "English", flag: "🇺🇸" },
  { code: "zh", value: "zh", label: "简体中文", flag: "🇨🇳" },
  { code: "pt", value: "pt", label: "Português", flag: "🇧🇷" },
  { code: "ru", value: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "zh-TW", value: "tw", label: "繁体", flag: "🇹🇼" },
  { code: "ro", value: "ro", label: "Română", flag: "🇷🇴" },
  { code: "es", value: "es", label: "español", flag: "🇪🇸" },
  { code: "sk", value: "sk", label: "Slovenčina", flag: "🇸🇰" },
  { code: "it", value: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "de", value: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "ja", value: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "cs", value: "cs", label: "Čeština", flag: "🇨🇿" },
];

// 未存过语言偏好时的默认语言（存量用户读 U_LOCALE，不受影响）
const DEFAULT_CODE = "en";

// 把后台 code（en / en-US / zh_CN / pt-BR …）折算成本地词表键。
// 没有对应词表时返回主语言子标签，$t 会自然回退到英文。
export function resolvePack(raw) {
  const v = String(raw || "")
    .toLowerCase()
    .replace(/_/g, "-");
  if (!v) return DEFAULT_CODE;
  if (/^zh\b/.test(v)) return /(hant|tw|hk|mo)/.test(v) ? "tw" : "zh";
  return v.split("-")[0];
}

function toEntry(item) {
  if (!item) return null;
  const code = String(item.code || "").trim();
  if (!code) return null;
  return {
    code,
    value: resolvePack(code),
    label: String(item.name || item.english_name || code).trim() || code,
    flag: String(item.flag || "").trim(),
  };
}

// 先按 code 精确匹配，再按词表键匹配（存量存的是 en-US，后台 code 可能是 en）
function findEntry(list, raw) {
  const want = String(raw || "")
    .toLowerCase()
    .replace(/_/g, "-");
  if (!want) return null;

  const exact = list.find((l) => l.code.toLowerCase() === want);
  if (exact) return exact;

  const pack = resolvePack(want);
  return list.find((l) => l.value === pack) || null;
}

export const i18nState = Vue.observable({
  list: FALLBACK_LOCALES.slice(),
  code: DEFAULT_CODE, // 传给后端的语言标识
  locale: DEFAULT_CODE, // 本地词表键
});

export function getLocales() {
  return i18nState.list;
}

export function getLocale() {
  return i18nState.locale;
}

export function getLangCode() {
  return i18nState.code;
}

export function getLocaleMeta(raw) {
  const key = raw == null ? i18nState.code : raw;
  return findEntry(i18nState.list, key) || i18nState.list[0] || FALLBACK_LOCALES[0];
}

export function setLocale(raw) {
  const entry = findEntry(i18nState.list, raw) || i18nState.list[0];
  if (!entry) return;

  i18nState.code = entry.code;
  i18nState.locale = entry.value;

  // U_LOCALE 记住用户选择；U_LANGUAGE 是各接口请求头 lang / Accept-Language 读的值
  localStorage.setItem("U_LOCALE", entry.code);
  localStorage.setItem("U_LANGUAGE", entry.code);
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.setAttribute("lang", entry.code);
  }
}

// 用后台返回的清单替换内置清单，并按新清单重新解析当前选择
export function applyLanguageList(items) {
  const list = (Array.isArray(items) ? items : []).map(toEntry).filter(Boolean);
  if (!list.length) return false;

  i18nState.list = list;
  setLocale(localStorage.getItem("U_LOCALE") || i18nState.code);
  return true;
}

export async function loadLanguages() {
  try {
    return applyLanguageList(await getLanguages());
  } catch (err) {
    return false; // 接口异常就继续用内置清单
  }
}

// 启动时先按存量偏好定位（此时还是内置清单），拿到接口后再校正
setLocale(localStorage.getItem("U_LOCALE") || localStorage.getItem("U_LANGUAGE") || DEFAULT_CODE);

// 取某个组件在某语言下的集中词表
function centralTable(locale, componentName) {
  if (!componentName) return null;
  const pack = messages[locale];
  return (pack && pack[componentName]) || null;
}

export default {
  install(VueCtor) {
    // 查找顺序：集中词表(当前语言) -> 组件词表(当前语言) -> 集中词表(en) -> 组件 en -> 组件 zh -> key
    VueCtor.prototype.$t = function translate(key) {
      const locale = i18nState.locale;
      const name = this.$options.name;

      const central = centralTable(locale, name);
      if (central && key in central) return central[key];

      const dict = this.$options.i18n;
      if (!dict) return key;

      const table = dict[locale];
      if (table && key in table) return table[key];

      const centralEn = centralTable("en", name);
      if (centralEn && key in centralEn) return centralEn[key];

      const en = dict.en || {};
      if (key in en) return en[key];

      const zh = dict.zh || {};
      return key in zh ? zh[key] : key;
    };

    VueCtor.prototype.$setLocale = setLocale;

    Object.defineProperty(VueCtor.prototype, "$locale", {
      get() {
        return i18nState.locale;
      },
    });

    // 语言变化要重拉后端文案时，watch 这个（两个 code 可能映射同一份词表）
    Object.defineProperty(VueCtor.prototype, "$langCode", {
      get() {
        return i18nState.code;
      },
    });
  },
};
